import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { canSee, Feature } from '../lib/features';
import type { LoginRequest, LoginResponse, Operator, ProfileResponse } from '../contracts';

interface AuthContextType {
  user: Operator | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

// Pages outside the operator login: no profile request.
const PUBLIC_PATHS = [/^\/review(\/|$)/, /^\/availability\//, /^\/checkin\//];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useCanSee() {
  const { user } = useAuth();
  return (feature: Feature) => canSee(user?.features, feature);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.removeItem('token');
    if (PUBLIC_PATHS.some((re) => re.test(window.location.pathname))) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get<ProfileResponse>('/auth/profile');
      setUser(response.data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const body: LoginRequest = { email, password };
      const response = await api.post<LoginResponse>('/auth/login', body);
      setUser(response.data.user);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => undefined);
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
